'use client';

import React, { useState, useEffect, useRef, startTransition } from 'react';
import { fetchMe } from "@/lib/fetchMe";
const logo = '/images/logo.webp';
import { IoSearch, IoCloseCircleSharp } from "react-icons/io5";
import { GiHamburgerMenu } from "react-icons/gi";
import Link from 'next/link';
import { useRouter, usePathname } from 'next/navigation';
import http from "@/lib/http";
import '../../../components/UserPanel/Home.css';
import { FaUserAlt } from "react-icons/fa";
import { RxDashboard } from "react-icons/rx";
import { GoPerson } from "react-icons/go";
import { MdLogout } from "react-icons/md";
import { MdKeyboardArrowDown } from "react-icons/md";
import { MdKeyboardArrowUp } from "react-icons/md";
import DropdownSidebar from '../../UserPanel/DropdownSidebar';
import DropdownCourseList from '../../UserPanel/DropdownCourseList';
import '../../../components/UserPanel/CoursePage/Course.css';
import { BsCart2 } from "react-icons/bs";
import { CgPathOutline, CgMenuGridR } from "react-icons/cg";
import { BsBookmarkHeart } from "react-icons/bs";
import { PiNotePencilBold, PiCertificateBold, PiBriefcase } from "react-icons/pi";
import { MdOutlineRateReview } from "react-icons/md";
const resolveImageUrl = img => {
  if (!img) return "";
  if (/^https?:\/\//i.test(img)) return img;
  return `https://api.hachion.co${img.startsWith("/") ? "" : "/"}${img}`;
};
async function ensureAvatarFromApi(email, setUserData) {
  if (!email) return;
  try {
    const {
      data
    } = await http.get(`https://api.hachion.co/api/v1/user/myprofile`, {
      params: {
        email
      }
    });
    const img = data?.profileImageUrl ? resolveImageUrl(data.profileImageUrl) : data?.profileImage ? resolveImageUrl(`/api/v1/user/profile/${data.profileImage}`) : "";
    if (img) {
      setUserData(prev => {
        const next = {
          ...(prev || {}),
          picture: img,
          name: prev?.name || data?.name || data?.userName || ""
        };
        try {
          const raw = localStorage.getItem("loginuserData");
          const stored = raw ? JSON.parse(raw) : {};
          localStorage.setItem("loginuserData", JSON.stringify({
            ...stored,
            ...next
          }));
        } catch {}
        return next;
      });
    }
  } catch {}
}
// Plain-element replacement for the former MUI styled(Avatar): same 40px
// circle / 2px #00AEEF border / white background, without shipping MUI +
// emotion in the navbar (and therefore in every public page's JS).
const profileAvatarStyle = {
  width: 40,
  height: 40,
  border: '2px solid #00AEEF',
  backgroundColor: '#ffffff',
  borderRadius: '50%',
  overflow: 'hidden',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  boxSizing: 'border-box'
};
const ProfileAvatar = ({ src, alt, children }) => (
  <div style={profileAvatarStyle}>
    {src ? (
      // eslint-disable-next-line @next/next/no-img-element
      <img src={src} alt={alt} width={40} height={40} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
    ) : children}
  </div>
);
const NavbarTop = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [courses, setCourses] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userData, setUserData] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  // Add state for logout message
  const [showLogoutMessage, setShowLogoutMessage] = useState(false);
  const dropdownRef = useRef();
  const router = useRouter();
  const pathname = usePathname();
  const userDropdownRef = useRef();
  // Matches production: clicking the logo while already on the Home page
  // does a full reload instead of Next.js's Link no-op (same-route
  // client-side navigations don't re-render anything by default).
  const handleLogoClick = (e) => {
    if (pathname === '/') {
      e.preventDefault();
      window.location.reload();
    }
  };
  // The course list only feeds the search box, so it is fetched on the first
  // focus/keystroke in a search input instead of on every page load (it used
  // to fire an API call and a navbar re-render during hydration).
  const coursesPromiseRef = useRef(null);
  const loadCourses = () => {
    if (!coursesPromiseRef.current) {
      coursesPromiseRef.current = http.get(`https://api.hachion.co/courses/names-and-categories`).then(res => {
        const list = res.data.map(course => ({
          ...course,
          courseName: course.courseName?.trim()
        }));
        setCourses(list);
        return list;
      }).catch(error => {
        console.error(error);
        coursesPromiseRef.current = null;
        return [];
      });
    }
    return coursesPromiseRef.current;
  };
  const handleCategorySelect = category => setSelectedCategory(category);
  const getCookie = name => {
    const escaped = name.replace(/([.$?*|{}()[\]\\/+^])/g, "\\$1");
    const match = document.cookie.match(new RegExp("(?:^|; )" + escaped + "=([^;]*)"));
    return match ? decodeURIComponent(match[1]) : null;
  };
  useEffect(() => {
    const storedUser = localStorage.getItem("loginuserData");
    const avatar = localStorage.getItem("avatar") || getCookie("avatar");
    if (storedUser) {
      let parsed = JSON.parse(storedUser);
      if (parsed && avatar && !parsed.picture) {
        parsed.picture = avatar;
        localStorage.setItem("loginuserData", JSON.stringify(parsed));
      }
      if (parsed) {
        // eslint-disable-next-line react-hooks/set-state-in-effect -- one-time hydration from localStorage (not readable during SSR)
        setUserData(parsed);
        setIsLoggedIn(true);
        if (!parsed.picture && parsed.email) ensureAvatarFromApi(parsed.email, setUserData);
      }
    }
  }, []);
  useEffect(() => {
    // Try to hydrate from localStorage + avatar cookie first
    const stored = localStorage.getItem("loginuserData");
    const avatarCookie = getCookie("avatar");
    if (stored) {
      try {
        const u = JSON.parse(stored);
        // If we have cookie avatar but no picture saved, merge it
        if (!u.picture && avatarCookie) {
          u.picture = avatarCookie;
          localStorage.setItem("loginuserData", JSON.stringify(u));
        }
        // eslint-disable-next-line react-hooks/set-state-in-effect -- one-time hydration from localStorage (not readable during SSR)
        setUserData(u);
        setIsLoggedIn(true);
        return; // already hydrated, no need to call /api/me
      } catch (e) {
        console.warn("[NavbarTop] JSON parse error for loginuserData in second effect:", e);
      }
    }

    // If no valid stored user, ask backend who is logged in (for Google OAuth, etc.)
    fetchMe().then(u => {
      if (!u) return;
      const toStore = {
        name: u.name || "",
        email: u.email || "",
        picture: u.picture || avatarCookie || ""
      };
      localStorage.setItem("loginuserData", JSON.stringify(toStore));
      // If you use this flag during OAuth flow, we can clean it up:
      localStorage.removeItem("pendingOAuth");
      setUserData(toStore);
      setIsLoggedIn(true);
    }).catch(err => console.error("[NavbarTop] /api/me failed:", err));
  }, []);
  useEffect(() => {
    // NavbarTop persists across client-side (App Router) navigations, so it
    // only mounts — and only reads localStorage — once per full page load.
    // Login.jsx, Register.jsx, and RegisterNext.jsx already write
    // loginuserData and dispatch a synthetic "storage" event after a
    // successful login/registration (native "storage" events don't fire in
    // the same tab that made the change, which is why they dispatch one
    // manually), but nothing here was listening for it — so a login that
    // completes via router.push (no full reload) left the header stuck on
    // its stale logged-out state until the user manually refreshed. This
    // re-syncs from localStorage whenever that event (same-tab synthetic or
    // real cross-tab) fires, reusing the same read logic as the mount
    // effects above instead of adding a second auth mechanism.
    const syncAuthFromStorage = () => {
      const stored = localStorage.getItem("loginuserData");
      if (!stored) {
        setUserData(null);
        setIsLoggedIn(false);
        return;
      }
      try {
        setUserData(JSON.parse(stored));
        setIsLoggedIn(true);
      } catch (e) {
        console.warn("[NavbarTop] JSON parse error for loginuserData in storage sync:", e);
      }
    };
    window.addEventListener("storage", syncAuthFromStorage);
    return () => window.removeEventListener("storage", syncAuthFromStorage);
  }, []);
  const deleteCookieEverywhere = name => {
    document.cookie = `${name}=; Max-Age=0; Path=/`;
    document.cookie = `${name}=; Max-Age=0; Path=/; Domain=hachion.co; Secure`;
    document.cookie = `${name}=; Max-Age=0; Path=/; Domain=.hachion.co; Secure`;
  };
  const handleLogout = async () => {
    try {
      await fetch(`https://api.hachion.co/api/logout`, {
        method: "POST",
        credentials: "include",
        redirect: "manual"
      });
    } catch {} finally {
      localStorage.clear();
      deleteCookieEverywhere("avatar");
      deleteCookieEverywhere("flow");
      setIsLoggedIn(false);
      setUserData(null);
      // Show logout success message
      setShowLogoutMessage(true);
      // Hide message after 3 seconds
      setTimeout(() => {
        setShowLogoutMessage(false);
      }, 3000);
      router.push("/");
    }
  };
  // Fuse indexes are built once per list and reused across keystrokes (they
  // were rebuilt for both lists on every keystroke, which delayed the next
  // paint of the typed character); the newest query wins if answers arrive
  // out of order.
  const fuseCacheRef = useRef({ courseList: null, courseFuse: null, blogList: null, blogFuse: null });
  const searchSeqRef = useRef(0);
  const handleSearchChange = async e => {
    const query = e.target.value;
    const seq = ++searchSeqRef.current;
    setSearchQuery(query);
    if (query.trim().length < 2) {
      setSearchResults([]);
      return;
    }
    // Loaded on first search only - fuse.js is not needed to render the page.
    const [{ default: Fuse }, courseList] = await Promise.all([import('fuse.js'), loadCourses()]);
    if (seq !== searchSeqRef.current) return;
    const cache = fuseCacheRef.current;
    if (cache.courseList !== courseList) {
      cache.courseList = courseList;
      cache.courseFuse = new Fuse(courseList, {
        keys: ["courseName"],
        threshold: 0.4
      });
    }
    if (cache.blogList !== blogs) {
      cache.blogList = blogs;
      cache.blogFuse = new Fuse(blogs, {
        keys: ["title"],
        threshold: 0.4
      });
    }
    const courseResults = cache.courseFuse.search(query).map(r => ({
      ...r.item,
      type: "course"
    }));
    const blogResults = cache.blogFuse.search(query).map(r => ({
      ...r.item,
      type: "blog"
    }));
    startTransition(() => setSearchResults([...courseResults, ...blogResults]));
  };  const handleCourseClick = item => {
    if (item.type === "course") {
      const courseSlug = item.courseName.toLowerCase().replace(/\s+/g, "-");
      const categorySlug = item.courseCategory.toLowerCase().replace(/\s+/g, "-");
      router.push(`/courses/${categorySlug}/${courseSlug}`);
    } else {
      const slug = item.title.toLowerCase().replace(/\s+/g, "-");
      router.push(`/blogs/${item.category_name}/${slug}-${item.id}`);
    }
    setSearchResults([]);
    setMobileSearchOpen(false);
    setSearchQuery("");
  };
  useEffect(() => {
    function handleClickOutside(e) {
      const searchBox = document.querySelector(".custom-search");
      const resultsBox = document.querySelector(".search-results");
      if (searchBox && !searchBox.contains(e.target) && resultsBox && !resultsBox.contains(e.target)) {
        setSearchResults([]);
      }
    }
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);
  useEffect(() => {
    document.body.style.overflow = drawerOpen ? "hidden" : "";
  }, [drawerOpen]);
  const handleMouseEnter = () => {
    clearTimeout(window.dropdownTimeout);
    setIsDropdownOpen(true);
  };
  const handleMouseLeave = () => {
    window.dropdownTimeout = setTimeout(() => {
      setIsDropdownOpen(false);
    }, 200);
  };
  const handleClickToggle = () => {
    setIsDropdownOpen(prev => !prev);
  };
  useEffect(() => {
    const handleClickOutside = e => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);
  return <>
      {/* Navbar */}
      <nav className="navbar navbar-expand-lg bg-white shadow-sm" style={{
      height: "80px"
    }}>
        <div className="container">
          <Link className="navbar-brand d-flex align-items-center" href="/" onClick={handleLogoClick}>
            <img src={logo} alt="logo" className="logo" width={180} height={48} />
          </Link>

          {/* Desktop Content */}
          <div className="collapse navbar-collapse d-none d-lg-flex" id="navbarScroll">
            <div className="navbar-nav my-lg-0 navbar-nav-scroll" ref={dropdownRef}>
              <div className="nav-item dropdown">
                <button className="nav-link" type="button" aria-expanded={isDropdownOpen} aria-haspopup="true" onClick={handleClickToggle} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} style={{
                color: "#000000",
                fontWeight: "500",
                background: "none",
                border: "none"
              }}>
                  Explore Courses{" "}
                  <span className="ms-1 arrow-icon" aria-hidden="true">
                    {isDropdownOpen ? <MdKeyboardArrowUp /> : <MdKeyboardArrowDown />}
                  </span>
                </button>
                {isDropdownOpen && <ul className="dropdown-menu custom-dropdown-menu show" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
                    <li>
                      <div className="course-content">
                        <div className="scrollable-category-list">
                          <DropdownSidebar onSelectCategory={handleCategorySelect} selectedCategory={selectedCategory} />
                        </div>
                        <div className="sidebar-right-container">
                          <DropdownCourseList category={selectedCategory} onCourseSelect={() => setIsDropdownOpen(false)} />
                          <li>
                            {/* Was a <button onClick={router.push(...)}> — no real href, so
                                a crawler without JS couldn't follow the desktop nav into
                                /courses at all (this dropdown is the only desktop entry
                                point into the course catalog). */}
                            <Link
                              href="/courses"
                              className="dropdown-all-btn"
                              style={{ textDecoration: "none", display: "inline-block" }}
                              onClick={() => setIsDropdownOpen(false)}
                            >
                              Explore All Courses
                            </Link>
                          </li>
                        </div>
                      </div>
                    </li>
                  </ul>}
              </div>
            </div>

            {/* Desktop Search */}
            <div className="search-container position-relative flex-grow-1 mx-3" style={{
            maxWidth: "500px"
          }}>
              <form className="d-flex flex-grow-1" role="search">
                <div className="input-group rounded-pill custom-search w-100" style={{
                overflow: "hidden",
                height: "48px",
                maxWidth: "400px"
              }}>
                  <input type="search" className="form-control border-0" placeholder="What would you like to learn?" value={searchQuery} onChange={handleSearchChange} onFocus={loadCourses} />
                  <button className="btn btn-info d-flex align-items-center justify-content-center search-btn" type="submit" aria-label="Search" onClick={e => e.preventDefault()}>
                    <IoSearch size={20} className="text-white" />
                  </button>
                </div>
              </form>
              {searchResults.length > 0 && <div className="search-results position-absolute bg-white shadow rounded w-100 mt-1">
                  {searchResults.map(item => <div key={item._id || item.id} className="p-2 border-bottom text-dark d-flex align-items-center" onClick={() => handleCourseClick(item)} style={{
                cursor: "pointer"
              }}>
                      <img src={resolveImageUrl(item.courseImage)} alt="" style={{
                  width: 40,
                  height: 40,
                  marginRight: 8
                }} />
                      {item.courseName || item.title}
                    </div>)}
                </div>}
            </div>
          </div>

          {/* Mobile Right Section */}
          <div className="d-flex align-items-center d-lg-none ms-auto">
            <button className="btn" onClick={() => setMobileSearchOpen(true)} aria-label="Open search">
              <IoSearch size={26} color="#00AEEF" />
            </button>
            {/* Show logout message for non-logged in users when they try to click cart */}
            {!isLoggedIn && showLogoutMessage && <div className="position-absolute top-100 start-50 translate-middle-x bg-success text-white px-3 py-2 rounded shadow-sm mt-2" style={{
            zIndex: 1060,
            whiteSpace: 'nowrap'
          }}>
                You are logged out successfully
              </div>}
            {isLoggedIn && <Link className="btn ms-2" aria-label="View cart" href="/userdashboard/order_history">
                <BsCart2 size={24} color="#000" />
              </Link>}
            <button className="btn ms-2" aria-label="Open menu" onClick={() => setDrawerOpen(true)}>
              <GiHamburgerMenu size={28} />
            </button>
          </div>

          {/* Desktop Right */}
          <div className="d-none d-lg-flex align-items-center gap-3">
            {!isLoggedIn ? <>
                <Link href="/login" prefetch={false} className="btn btn-outline-info rounded-pill px-3 fw-bold text-nowrap">
                  Log in
                </Link>
                <Link href="/register" prefetch={false} className="btn btn-info rounded-pill px-3 text-white fw-bold text-nowrap">
                  Sign up
                </Link>
                {/* Show logout message for desktop when not logged in. Laid
                    out as a normal flex item (gap-3, same as Log in/Sign up)
                    instead of position:fixed with a hardcoded right offset -
                    that offset was a guess at where the buttons would land,
                    which broke (covered Log in) at any viewport width or
                    button size it didn't happen to match. Flowing with the
                    flex row instead means it always sits in the actual gap
                    the layout leaves between the search area and the
                    buttons, on every breakpoint. */}
                {showLogoutMessage && <div className="bg-success text-white px-3 py-2 rounded shadow-sm text-nowrap" style={{
              zIndex: 10
            }}>
                    You are logged out successfully
                  </div>}
              </> : <div className="d-flex align-items-center gap-3">
                <Link className="btn" aria-label="View cart" href="/userdashboard/order_history">
                  <BsCart2 size={28} />
                </Link>
                <div className="dropdown" ref={userDropdownRef}>
                  <button className="btn d-flex align-items-center" onClick={() => setIsOpen(!isOpen)} style={{
                background: "transparent",
                border: "none"
              }}>
                    {userData?.picture ? <ProfileAvatar src={userData.picture} alt="user avatar" /> : <ProfileAvatar>
                        <FaUserAlt size={20} color="#b3b3b3" />
                      </ProfileAvatar>}
                    <span className="ms-2">{userData?.name || "User"}</span>
                    <span className="ms-1 arrow-icon" onClick={e => {
                  e.stopPropagation();
                  setIsOpen(!isOpen);
                }} style={{
                  cursor: "pointer"
                }}>
                      {isOpen ? <MdKeyboardArrowUp /> : <MdKeyboardArrowDown />}
                    </span>
                  </button>
                  {isOpen && <ul className="dropdown-menu dropdown-menu-end show">
                      <li>
                        <Link className="dropdown-item" href="/userdashboard/dashboard" onClick={() => setIsOpen(false)}>
                          <RxDashboard /> Dashboard
                        </Link>
                      </li>
                      <li>
                        <Link className="dropdown-item" href="/userdashboard/profile" onClick={() => setIsOpen(false)}>
                          <GoPerson /> Profile
                        </Link>
                      </li>
                      <li>
                        <Link className="dropdown-item" href="/userdashboard/enrolls" onClick={() => setIsOpen(false)}>
                          <PiNotePencilBold /> Enrolls
                        </Link>
                      </li>
                      <li>
                        <Link className="dropdown-item" href="/userdashboard/wishlist" onClick={() => setIsOpen(false)}>
                          <BsBookmarkHeart /> Wishlist
                        </Link>
                      </li>
                      <li>
                        <Link className="dropdown-item" href="/my-bookmarks" onClick={() => setIsOpen(false)}>
                          <BsBookmarkHeart /> Saved Blogs
                        </Link>
                      </li>
                      <li>
                        <Link className="dropdown-item" href="/userdashboard/order_history" onClick={() => setIsOpen(false)}>
                          <BsCart2 /> Orders
                        </Link>
                      </li>
                      <li>
                        <Link className="dropdown-item" href="/userdashboard/certificate" onClick={() => setIsOpen(false)}>
                          <PiCertificateBold /> Certificates
                        </Link>
                      </li>
                      <li>
                        <Link className="dropdown-item" href="/userdashboard/review" onClick={() => setIsOpen(false)}>
                          <MdOutlineRateReview /> Review
                        </Link>
                      </li>
                      <li>
                        <Link className="dropdown-item" href="/userdashboard/pathfinder" onClick={() => setIsOpen(false)}>
                          <CgPathOutline /> Pathfinder
                        </Link>
                      </li>
                      <li>
                        <button className="dropdown-item" onClick={() => {
                    setIsOpen(false);
                    handleLogout();
                  }}>
                          <MdLogout /> Logout
                        </button>
                      </li>
                    </ul>}
                </div>
              </div>}
          </div>
        </div>
      </nav>

      {/* Overlay and Drawer */}
      {drawerOpen && <div className="overlay" onClick={() => setDrawerOpen(false)} />}
      <div className={`sidebar-drawer ${drawerOpen ? "open" : ""}`}>
        <div className="category-drawer-header">
          <Link href="/" onClick={(e) => { handleLogoClick(e); setDrawerOpen(false); }}>
            <img src={logo} alt="logo" className="logo" width={180} height={48} />
          </Link>
          <button className="filter-close-btn" aria-label="Close menu" onClick={() => setDrawerOpen(false)}>
            ✕
          </button>
        </div>
        <div className="drawer-body">
          {isLoggedIn ? <>
              <div className="d-flex align-items-center mb-3">
                {userData?.picture ? <ProfileAvatar src={userData.picture} alt="user avatar" /> : <ProfileAvatar>
                    <FaUserAlt size={20} color="#b3b3b3" />
                  </ProfileAvatar>}
                <span className="ms-2">{userData?.name || "User"}</span>
              </div>

              <div className="drawer-item" onClick={() => {
            router.push("/userdashboard/dashboard");
            setDrawerOpen(false);
          }}>
                <RxDashboard /> Dashboard
              </div>
              <div className="drawer-item" onClick={() => {
            router.push("/userdashboard/profile");
            setDrawerOpen(false);
          }}>
                <GoPerson /> Profile
              </div>
              <div className="drawer-item" onClick={() => {
            router.push("/userdashboard/enrolls");
            setDrawerOpen(false);
          }}>
                <PiNotePencilBold /> Enrolls
              </div>
              <div className="drawer-item" onClick={() => {
            router.push("/userdashboard/wishlist");
            setDrawerOpen(false);
          }}>
                <BsBookmarkHeart /> Wishlist
              </div>
              <div className="drawer-item" onClick={() => {
            router.push("/my-bookmarks");
            setDrawerOpen(false);
          }}>
                <BsBookmarkHeart /> Saved Blogs
              </div>
              <div className="drawer-item" onClick={() => {
            router.push("/userdashboard/order_history");
            setDrawerOpen(false);
          }}>
                <BsCart2 /> Orders
              </div>
              <div className="drawer-item" onClick={() => {
            router.push("/userdashboard/certificate");
            setDrawerOpen(false);
          }}>
                <PiCertificateBold /> Certificates
              </div>
              <div className="drawer-item" onClick={() => {
            router.push("/userdashboard/review");
            setDrawerOpen(false);
          }}>
                <MdOutlineRateReview /> Review
              </div>
              <div className="drawer-item" onClick={() => {
            router.push("/userdashboard/pathfinder");
            setDrawerOpen(false);
          }}>
                <CgPathOutline /> Pathfinder
              </div>

              <Link href="/courses" prefetch={false} className="drawer-item" style={{ textDecoration: "none" }} onClick={() => setDrawerOpen(false)}>
                Explore Courses
              </Link>
              <Link href="/corporate" prefetch={false} className="drawer-item" style={{ textDecoration: "none" }} onClick={() => setDrawerOpen(false)}>
                Corporate Training
              </Link>

              <button className="btn btn-info rounded-pill w-100 text-white mt-3" onClick={() => {
            setDrawerOpen(false);
            handleLogout();
          }}>
                <MdLogout /> Logout
              </button>
            </> : <>
              <Link href="/courses" prefetch={false} className="drawer-item" style={{ textDecoration: "none" }} onClick={() => setDrawerOpen(false)}>
                Explore Courses
              </Link>
              <Link href="/corporate" prefetch={false} className="drawer-item" style={{ textDecoration: "none" }} onClick={() => setDrawerOpen(false)}>
                Corporate Training
              </Link>
              <Link href="/login" prefetch={false} className="btn btn-outline-info rounded-pill w-100 mt-3 fw-bold" onClick={() => setDrawerOpen(false)}>
                Log In
              </Link>
              <Link href="/register" prefetch={false} className="btn btn-info rounded-pill w-100 mt-2 text-white fw-bold" onClick={() => setDrawerOpen(false)}>
                Sign Up
              </Link>
            </>}
        </div>
      </div>

      {/* Mobile Search Popup */}
      {mobileSearchOpen && <div className="position-fixed top-0 start-0 w-100 h-100 bg-white p-3" style={{
      zIndex: 1050
    }}>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div className="input-group rounded-pill custom-search w-100" style={{
          overflow: "hidden",
          height: "48px"
        }}>
              <input type="search" className="form-control border-0" placeholder="What would you like to learn?" value={searchQuery} onChange={handleSearchChange} onFocus={loadCourses} autoFocus />
              <button className="btn btn-info d-flex align-items-center justify-content-center search-btn" type="submit" aria-label="Open search" onClick={e => e.preventDefault()}>
                <IoSearch size={20} className="text-white" />
              </button>
            </div>
            <button className="filter-close-btn ms-2" aria-label="Close search" onClick={() => {
          setMobileSearchOpen(false);
          setSearchQuery("");
          setSearchResults([]);
        }}>
              ✕
            </button>
          </div>

          <div className="overflow-auto" style={{
        maxHeight: "85vh"
      }}>
            {searchResults.map(item => <div key={item._id || item.id} className="p-2 border-bottom d-flex align-items-center" onClick={() => handleCourseClick(item)} style={{
          cursor: "pointer"
        }}>
                <img src={resolveImageUrl(item.courseImage)} alt="" style={{
            width: 40,
            height: 40,
            marginRight: 10
          }} />
                {item.courseName || item.title}
              </div>)}
          </div>
        </div>}
    </>;
};
export default React.memo(NavbarTop);
